import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileArchive,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  criarSitesEmMassa,
  custoDoLote,
  lerZipDeLeads,
  progressoDaGeracao,
  type AndamentoCriacao,
  type LeadDoZip,
  type ProblemaNoZip,
  type ResultadoCriacao,
} from '../lib/criacao-massa';
import { api } from '../lib/api';

/**
 * ============================================================================
 * CRIAÇÃO DE SITES EM MASSA
 * ============================================================================
 *
 * A tela irmã da importação, e a diferença precisa estar visível ANTES do clique:
 *
 *   Importação  recebe sites prontos e os publica. Não custa nada.
 *   Criação     recebe leads (`dados.json` + fotos) e manda a IA escrever cada
 *               site. Cada linha do lote é uma chamada paga.
 *
 * Por isso esta tela mostra três coisas que a outra não tem: quanto o modo atual
 * gasta (ou se está em `template`, e portanto não gasta), o custo REAL do lote
 * enquanto ele roda, e a contagem de fotos que vão junto.
 *
 * OS NÚMEROS DE GASTO NÃO SÃO ESTIMATIVA
 *
 * Vêm de `uso_ia`, uma linha por chamada, com os tokens que o próprio Gemini
 * reportou — somados por lote no servidor. Esta aba não calcula custo nenhum: a
 * versão anterior desta ideia gravava R$ 0,18 fixos por site lidos do
 * localStorage, e a tela somava isso e chamava de gasto.
 */

type Fase = 'ocioso' | 'lendo' | 'pronto' | 'criando' | 'criado';

const brl = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

export const CriacaoEmMassa: React.FC = () => {
  const [fase, setFase] = useState<Fase>('ocioso');
  const [leads, setLeads] = useState<LeadDoZip[]>([]);
  const [problemas, setProblemas] = useState<ProblemaNoZip[]>([]);
  const [lote, setLote] = useState('');
  const [andamento, setAndamento] = useState<AndamentoCriacao>({
    etapa: 'cadastrando',
    feitos: 0,
    total: 0,
    atual: '',
  });
  const [resultado, setResultado] = useState<ResultadoCriacao | null>(null);
  const [fila, setFila] = useState<Awaited<ReturnType<typeof progressoDaGeracao>> | null>(null);
  const [custo, setCusto] = useState<Awaited<ReturnType<typeof custoDoLote>> | null>(null);
  const [config, setConfig] = useState<Awaited<ReturnType<typeof api.chamar<'ia.config'>>> | null>(
    null,
  );
  const [arrastando, setArrastando] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  /*
    Qual modo a esteira está usando, perguntado ao SERVIDOR.

    É a diferença entre "vou gastar" e "vou receber template", e ela não pode ser
    adivinhada por esta aba: `config_ia.modo_geracao` é o interruptor da tela de
    IA. Sem mostrar isto aqui, quem manda 500 leads no modo `template` recebe 500
    sites de template e passa o dia procurando o erro na chave.
  */
  useEffect(() => {
    void (async () => {
      try {
        setConfig(await api.chamar('ia.config', {}));
      } catch (e) {
        console.warn('[criacao] não consegui ler a configuração de IA:', e);
      }
    })();
  }, []);

  /*
    O andamento e o gasto vêm do servidor a cada 5 s.

    O worker gera fora desta aba: um contador local pararia no F5, e o custo
    somado aqui seria a estimativa que este repositório já teve e que mentia.
  */
  const atualizar = useCallback(async (loteAtual: string) => {
    try {
      setFila(await progressoDaGeracao());
      if (loteAtual) setCusto(await custoDoLote(loteAtual));
    } catch (e) {
      console.warn('[criacao] não consegui ler o andamento:', e);
    }
  }, []);

  useEffect(() => {
    if (fase !== 'criado') return;
    void atualizar(lote);
    const t = setInterval(() => void atualizar(lote), 5000);
    return () => clearInterval(t);
  }, [fase, lote, atualizar]);

  const receberArquivos = async (lista: File[]) => {
    const zips = lista.filter((f) => f.name.toLowerCase().endsWith('.zip'));
    if (zips.length === 0) {
      toast.error('Arraste os .zip exportados pela tela de leads.');
      return;
    }

    setFase('lendo');
    setLeads([]);
    setProblemas([]);
    setResultado(null);
    setCusto(null);

    const achados: LeadDoZip[] = [];
    const ruins: ProblemaNoZip[] = [];
    for (const zip of zips) {
      try {
        const r = await lerZipDeLeads(zip);
        achados.push(...r.leads);
        ruins.push(...r.problemas);
      } catch (e) {
        console.error(`[criacao] falha ao abrir "${zip.name}":`, e);
        ruins.push({ pasta: zip.name, motivo: 'não consegui abrir o ZIP' });
      }
    }

    setLeads(achados);
    setProblemas(ruins);
    setLote(sugerirLote(zips));
    setFase(achados.length ? 'pronto' : 'ocioso');
    if (!achados.length) toast.error('Nenhuma pasta com dados.json nesses arquivos.');
  };

  const criar = async () => {
    const rotulo = lote.trim() || sugerirLote([]);
    setLote(rotulo);
    setFase('criando');
    try {
      const r = await criarSitesEmMassa(rotulo, leads, setAndamento);
      setResultado(r);
      setFase('criado');
      await atualizar(rotulo);
      toast.success(
        `${r.enfileirados} site(s) na fila da IA. Pode fechar esta aba — o worker continua.`,
      );
    } catch (e) {
      console.error('[criacao] falha no envio:', e);
      toast.error(e instanceof Error ? e.message : 'Falha ao criar o lote.');
      setFase('pronto');
    }
  };

  const totalFotos = leads.reduce((s, l) => s + l.fotos.length, 0);
  const semFotos = leads.filter((l) => l.fotos.length === 0).length;
  /*
    Quantos leads vêm SEM ramo — e por que isso aparece antes do clique.

    Medido no export real da captação: em 5 vidraçarias, 3 trazem só
    `types: ["establishment", "point_of_interest"]` e categoria de WhatsApp
    vazia. Não há ramo para inventar sem mentir, e o efeito é direto no
    resultado: a direção de arte do prompt casa por termo do ramo e do nome, e
    sem ramo esses sites saem no estilo genérico. Dizer isso aqui é mais barato
    que descobrir olhando 300 sites publicados.
  */
  const semCategoria = leads.filter((l) => !l.categoria).length;
  const totalNaFila = fila ? fila.pendente + fila.processando + fila.concluido + fila.falhou : 0;
  const pct = totalNaFila ? Math.round((fila!.concluido / totalNaFila) * 100) : 0;
  const modoTemplate = config?.modoGeracao === 'template';

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-gray-900">
          <Sparkles size={22} className="text-indigo-600" />
          Criação de sites em massa
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          Arraste os ZIPs exportados pela tela de leads. Cada pasta com{' '}
          <code className="rounded bg-gray-100 px-1 font-mono text-xs">dados.json</code> vira um{' '}
          <b>cliente cadastrado</b> e um <b>site escrito pela IA</b>, com as fotos da pasta. É
          diferente da “Importação em massa”, que recebe sites já prontos e só publica.
        </p>
      </header>

      {/* ------------------------------------------------- o modo da esteira --- */}
      {config && (
        <section
          className={`flex items-start gap-3 rounded-xl border p-4 text-xs leading-relaxed ${
            modoTemplate
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-indigo-100 bg-indigo-50 text-indigo-900'
          }`}
        >
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          {modoTemplate ? (
            <span>
              A esteira está em <b>modo template</b>: este lote NÃO vai chamar a IA e todos os
              sites sairão de um dos cinco modelos. Para gerar com IA, mude o modo na tela{' '}
              <b>IA</b>.
            </span>
          ) : (
            <span>
              A esteira está em <b>modo IA</b>, com o modelo{' '}
              <code className="rounded bg-white/60 px-1 font-mono">{config.modelo}</code> e a chave
              vinda {config.fonteChave === 'banco' ? 'do banco' : 'do ambiente'}. Cada site é uma
              chamada paga — o gasto real aparece abaixo enquanto o lote roda.{' '}
              {config.cota
                ? `Cota diária: ${config.cota.usadoHoje} de ${config.cota.limiteDia}.`
                : 'Sem cota diária cadastrada: a IA roda sem teto.'}
            </span>
          )}
        </section>
      )}

      {/* --------------------------------------------------------- soltar --- */}
      {(fase === 'ocioso' || fase === 'lendo') && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            void receberArquivos(Array.from(e.dataTransfer.files));
          }}
          onClick={() => arquivoRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
            arrastando ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {fase === 'lendo' ? (
            <>
              <Loader2 className="mx-auto animate-spin text-gray-400" size={32} />
              <p className="mt-3 text-sm text-gray-600">Lendo os ZIPs…</p>
            </>
          ) : (
            <>
              <FileArchive className="mx-auto text-gray-400" size={32} />
              <p className="mt-3 text-sm font-medium text-gray-900">
                Arraste os ZIPs de leads aqui, ou clique para escolher
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Uma pasta por lead, com <code className="font-mono">dados.json</code>,{' '}
                <code className="font-mono">foto_1.jpg</code>… e{' '}
                <code className="font-mono">foto_perfil_whatsapp.jpg</code>. Nada é enviado antes de
                você conferir.
              </p>
            </>
          )}
          <input
            ref={arquivoRef}
            type="file"
            accept=".zip,application/zip"
            multiple
            hidden
            onChange={(e) => {
              const l = e.target.files;
              const fs: File[] = l ? Array.from({ length: l.length }, (_, i) => l[i]!) : [];
              void receberArquivos(fs);
              if (arquivoRef.current) arquivoRef.current.value = '';
            }}
          />
        </div>
      )}

      {/* ------------------------------------------------- o que foi lido --- */}
      {(fase === 'pronto' || fase === 'criando' || fase === 'criado') && (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <header className="flex items-center gap-4 border-b border-gray-100 px-5 py-4">
            <div className="mr-auto">
              <h2 className="text-sm font-semibold text-gray-900">
                {leads.length} lead{leads.length === 1 ? '' : 's'} pronto
                {leads.length === 1 ? '' : 's'} para virar site
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {totalFotos} foto(s) sobem junto
                {semFotos > 0 && (
                  <>
                    {' · '}
                    <span className="text-amber-700">
                      {semFotos} sem foto nenhuma (o site sai com imagem de banco)
                    </span>
                  </>
                )}
                {semCategoria > 0 && (
                  <>
                    {' · '}
                    <span className="text-amber-700">
                      {semCategoria} sem ramo no dados.json (design genérico)
                    </span>
                  </>
                )}
              </p>
            </div>

            {fase === 'pronto' && (
              <>
                <label className="text-xs text-gray-500">
                  Lote
                  <input
                    value={lote}
                    onChange={(e) => setLote(e.target.value)}
                    className="ml-2 w-44 rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-xs text-gray-900 outline-none focus:border-gray-400"
                  />
                </label>
                <button
                  onClick={() => void criar()}
                  className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  <Sparkles size={15} />
                  Criar {leads.length} site{leads.length === 1 ? '' : 's'}
                </button>
                <button
                  onClick={() => {
                    setFase('ocioso');
                    setLeads([]);
                    setProblemas([]);
                  }}
                  aria-label="Descartar"
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                >
                  <X size={16} />
                </button>
              </>
            )}
          </header>

          {fase === 'criando' && (
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" />
                  {andamento.etapa === 'cadastrando' && 'Cadastrando clientes'}
                  {andamento.etapa === 'fotos' && 'Subindo fotos'}
                  {andamento.etapa === 'enfileirando' && 'Enfileirando a geração'}
                  {' — '}
                  {andamento.feitos} de {andamento.total}
                  {andamento.atual && <span className="text-gray-400">· {andamento.atual}</span>}
                </span>
                <span className="font-mono">
                  {Math.round((andamento.feitos / Math.max(1, andamento.total)) * 100)}%
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full bg-indigo-600 transition-all"
                  style={{ width: `${(andamento.feitos / Math.max(1, andamento.total)) * 100}%` }}
                />
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Só o envio precisa da aba aberta. A geração acontece no worker, depois.
              </p>
            </div>
          )}

          {resultado && (
            <p className="flex items-start gap-2 border-b border-green-200 bg-green-50 px-5 py-3 text-xs leading-relaxed text-green-800">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <span>
                <b>{resultado.clientesCriados} cliente(s) cadastrado(s)</b>,{' '}
                {resultado.fotosEnviadas} foto(s) no R2 e {resultado.enfileirados} site(s) na fila
                da IA.
                {resultado.jaExistiam > 0 && (
                  <>
                    {' '}
                    {resultado.jaExistiam} já existiam e <b>não</b> foram gerados de novo — regerar
                    é um botão na tela do cliente, e cada geração custa.
                  </>
                )}
                {resultado.fotosFalhadas > 0 && (
                  <> {resultado.fotosFalhadas} foto(s) falharam; os sites saem sem elas.</>
                )}
              </span>
            </p>
          )}

          <ul className="max-h-72 divide-y divide-gray-50 overflow-y-auto">
            {leads.slice(0, 300).map((l) => (
              <li key={l.pasta} className="flex items-center gap-3 px-5 py-2.5 text-xs">
                <span className="flex-1 truncate font-medium text-gray-900">{l.nome}</span>
                <span className="truncate text-[11px] text-gray-400">{l.categoria ?? '—'}</span>
                <span className="flex shrink-0 items-center gap-1 tabular-nums text-gray-400">
                  <ImageIcon size={12} />
                  {l.fotos.length}
                  {l.fotosIgnoradas > 0 && (
                    <span className="text-gray-300">(+{l.fotosIgnoradas} fora do teto)</span>
                  )}
                </span>
              </li>
            ))}
            {leads.length > 300 && (
              <li className="px-5 py-2.5 text-xs text-gray-400">
                …e mais {leads.length - 300}. Todos entram; a lista só mostra os 300 primeiros.
              </li>
            )}
          </ul>
        </section>
      )}

      {/* --------------------------------------------------- o que falhou --- */}
      {(problemas.length > 0 || (resultado?.falhas.length ?? 0) > 0) && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="flex items-center gap-2 text-xs font-semibold text-amber-900">
            <AlertCircle size={14} />
            {problemas.length + (resultado?.falhas.length ?? 0)} aviso(s)
          </h3>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {[...problemas, ...(resultado?.falhas ?? [])].slice(0, 20).map((p, i) => (
              <li key={`${p.pasta}-${i}`}>
                <span className="font-mono">{p.pasta}</span> — {p.motivo}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --------------------------------- o worker gerando, e o que gastou --- */}
      {fase === 'criado' && fila && (
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">A IA está escrevendo os sites</h2>
            <span className="font-mono text-xs text-gray-500">
              {fila.concluido} de {totalNaFila}
            </span>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Numero rotulo="Na fila" valor={fila.pendente} />
            <Numero rotulo="Gerando" valor={fila.processando} destaque={fila.processando > 0} />
            <Numero rotulo="Publicados" valor={fila.concluido} />
            <Numero rotulo="Falharam" valor={fila.falhou} alerta={fila.falhou > 0} />
          </dl>

          {/* ------------------------------------------------ o gasto real --- */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Gasto real deste lote
            </h3>
            {custo ? (
              <>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                  <Numero rotulo="Chamadas" valor={custo.chamadas} />
                  <Numero rotulo="Tokens" valor={custo.tokensTotal} />
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Custo
                    </dt>
                    <dd className="mt-0.5 text-lg font-bold tabular-nums text-gray-900">
                      {brl(custo.custoBrl)}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      Por site
                    </dt>
                    <dd className="mt-0.5 text-lg font-bold tabular-nums text-gray-900">
                      {custo.custoMedioPorSiteBrl === null
                        ? '—'
                        : brl(custo.custoMedioPorSiteBrl)}
                    </dd>
                  </div>
                </dl>
                {custo.chamadasSemPreco > 0 && (
                  <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
                    {custo.chamadasSemPreco} chamada(s) usaram um modelo <b>sem preço cadastrado</b>
                    : elas gastaram e <b>não</b> entram na soma acima. Cadastre o preço na tela de
                    IA para o número fechar com a fatura.
                  </p>
                )}
                {custo.falhas > 0 && (
                  <p className="mt-2 text-[11px] text-gray-500">
                    {custo.falhas} chamada(s) falharam — os sites correspondentes saíram pelo
                    template.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-2 text-xs text-gray-400">
                Nenhuma chamada registrada ainda neste lote.
              </p>
            )}
            <p className="mt-4 text-[11px] leading-relaxed text-gray-400">
              O valor vem de <code className="font-mono">uso_ia</code>: uma linha por chamada, com
              os tokens que o próprio Gemini reportou e o preço cadastrado do modelo. Não é
              estimativa por site nem contador desta aba — fechar e voltar mostra o gasto de onde
              ele estiver.
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

const Numero: React.FC<{
  rotulo: string;
  valor: number;
  destaque?: boolean;
  alerta?: boolean;
}> = ({ rotulo, valor, destaque, alerta }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2">
    <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{rotulo}</dt>
    <dd
      className={`mt-0.5 text-lg font-bold tabular-nums ${
        alerta ? 'text-red-600' : destaque ? 'text-indigo-600' : 'text-gray-900'
      }`}
    >
      {valor.toLocaleString('pt-BR')}
    </dd>
  </div>
);

/**
 * Um rótulo de lote legível, derivado do nome do ZIP.
 *
 * Sem data no rótulo: `new Date()` aqui daria dois nomes diferentes para o mesmo
 * ZIP enviado duas vezes, e o rótulo é o que separa o gasto de um lote do gasto
 * do outro — dois nomes para o mesmo envio quebrariam exatamente essa conta.
 */
function sugerirLote(zips: File[]): string {
  const base = zips[0]?.name.replace(/\.zip$/i, '') ?? 'criacao';
  return (
    base
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'criacao'
  );
}
